import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Icon, Message, Confirm, Label, Loader, Segment, Header, } from 'semantic-ui-react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useApiTokensClient } from './apiTokens.client';
import PageLayout from '../components/PageLayout';
import { Key } from 'lucide-react';
const ApiTokensPage = () => {
    const { isLoaded: authLoaded, isSignedIn } = useAuth();
    const { isLoaded: orgLoaded } = useOrganization();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [name, setName] = useState('');
    const [domainsInput, setDomainsInput] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [tokenToDelete, setTokenToDelete] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
    const [copiedTokenId, setCopiedTokenId] = useState(null);
    const client = useApiTokensClient();
    useEffect(() => {
        if (authLoaded && orgLoaded && isSignedIn) {
            loadTokens();
        }
    }, [authLoaded, orgLoaded, isSignedIn]);
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);
    if (!authLoaded || !orgLoaded) {
        return (React.createElement(PageLayout, { title: "API Tokens", subtitle: "Loading...", icon: React.createElement(Key, { className: "w-7 h-7" }) },
            React.createElement(Segment, { style: { padding: '50px', textAlign: 'center' } },
                React.createElement(Loader, { active: true, inline: "centered" }, "Loading..."))));
    }
    const loadTokens = async () => {
        try {
            setLoading(true);
            const result = await client.listTokens();
            setTokens(result);
        }
        catch (error) {
            setErrorMessage('Failed to load API tokens');
        }
        finally {
            setLoading(false);
        }
    };
    const handleCreate = async () => {
        try {
            setLoading(true);
            const allowedDomains = domainsInput
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean);
            if (allowedDomains.length === 0) {
                setErrorMessage('Please specify at least one allowed domain');
                setLoading(false);
                return;
            }
            const created = await client.createToken({ name, allowedDomains });
            setNewlyCreatedToken(created.token);
            setSuccessMessage('API token created. Copy the token now -- it will be masked after this.');
            await loadTokens();
            setModalVisible(false);
            setName('');
            setDomainsInput('');
        }
        catch (error) {
            setErrorMessage('Failed to create API token');
        }
        finally {
            setLoading(false);
        }
    };
    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await client.deleteToken(id);
            setSuccessMessage('API token deleted');
            await loadTokens();
        }
        catch (error) {
            setErrorMessage('Failed to delete API token');
        }
        finally {
            setLoading(false);
            setDeleteConfirmOpen(false);
            setTokenToDelete(null);
        }
    };
    const handleCopy = (text, tokenId) => {
        navigator.clipboard.writeText(text);
        setCopiedTokenId(tokenId);
        setTimeout(() => setCopiedTokenId(null), 2000);
    };
    const handleModalCancel = () => {
        setModalVisible(false);
        setName('');
        setDomainsInput('');
    };
    const isFormValid = name.trim().length >= 2 && domainsInput.trim().length > 0;
    return (React.createElement(PageLayout, { title: "API Tokens", subtitle: "Create tokens so external websites can call your forms via the API.", icon: React.createElement(Key, { className: "w-7 h-7" }), headerActions: React.createElement(Button, { primary: true, onClick: () => setModalVisible(true) },
            React.createElement(Icon, { name: "plus" }),
            "Create Token") },
        successMessage && (React.createElement(Message, { success: true, onDismiss: () => setSuccessMessage('') },
            React.createElement(Message.Header, null, "Success"),
            React.createElement("p", null, successMessage))),
        errorMessage && (React.createElement(Message, { error: true, onDismiss: () => setErrorMessage('') },
            React.createElement(Message.Header, null, "Error"),
            React.createElement("p", null, errorMessage))),
        newlyCreatedToken && (React.createElement(Message, { info: true, onDismiss: () => setNewlyCreatedToken(null) },
            React.createElement(Message.Header, null, "Your new API token"),
            React.createElement("p", { className: "flex items-center gap-2 mt-2" },
                React.createElement("code", { className: "bg-gray-100 px-3 py-1 rounded text-sm font-mono break-all" }, newlyCreatedToken),
                React.createElement(Button, { size: "mini", icon: "copy", onClick: () => handleCopy(newlyCreatedToken, 'new'), title: "Copy token" })),
            React.createElement("p", { className: "text-sm text-gray-600 mt-1" }, "This is the only time the full token is shown. Copy it now."))),
        loading && React.createElement(Loader, { active: true }, "Loading API tokens..."),
        tokens.length > 0 && (React.createElement("div", { className: "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" },
            React.createElement(Table, { basic: "very", className: "!border-0 !m-0" },
                React.createElement(Table.Header, null,
                    React.createElement(Table.Row, null,
                        React.createElement(Table.HeaderCell, null, "Name"),
                        React.createElement(Table.HeaderCell, null, "Token"),
                        React.createElement(Table.HeaderCell, null, "Allowed Domains"),
                        React.createElement(Table.HeaderCell, null, "Created"),
                        React.createElement(Table.HeaderCell, null, "Actions"))),
                React.createElement(Table.Body, null, tokens.map((t) => (React.createElement(Table.Row, { key: t.id },
                    React.createElement(Table.Cell, null,
                        React.createElement("strong", null, t.name)),
                    React.createElement(Table.Cell, null,
                        React.createElement("code", { className: "bg-gray-100 px-2 py-0.5 rounded text-xs font-mono text-gray-500" }, t.token)),
                    React.createElement(Table.Cell, null,
                        React.createElement("div", { className: "flex flex-wrap gap-1" }, t.allowedDomains.map((domain, i) => (React.createElement(Label, { key: i, size: "small", basic: true }, domain))))),
                    React.createElement(Table.Cell, null, new Date(t.createdAt).toLocaleDateString()),
                    React.createElement(Table.Cell, null,
                        React.createElement(Button, { icon: "trash", color: "red", size: "mini", onClick: () => {
                                setTokenToDelete(t.id);
                                setDeleteConfirmOpen(true);
                            }, title: "Delete token" }))))))))),
        tokens.length === 0 && !loading && (React.createElement(Segment, { placeholder: true },
            React.createElement(Header, { icon: true },
                React.createElement(Key, { className: "w-12 h-12 mx-auto mb-2 text-gray-400" }),
                "No API tokens yet"),
            React.createElement("p", { className: "text-gray-500" }, "Create a token to allow external websites to use your forms."),
            React.createElement(Button, { primary: true, onClick: () => setModalVisible(true) }, "Create your first token"))),
        React.createElement(Modal, { open: modalVisible, onClose: handleModalCancel, size: "small" },
            React.createElement(Modal.Header, null,
                React.createElement(Icon, { name: "key" }),
                "Create API Token"),
            React.createElement(Modal.Content, null,
                React.createElement(Form, null,
                    React.createElement(Form.Field, { required: true },
                        React.createElement("label", null, "Token Name"),
                        React.createElement(Input, { placeholder: "e.g., My Website Integration", value: name, onChange: (e) => setName(e.target.value) })),
                    React.createElement(Form.Field, { required: true },
                        React.createElement("label", null, "Allowed Domains"),
                        React.createElement(Input, { placeholder: "e.g., https://example.com, https://*.mysite.com", value: domainsInput, onChange: (e) => setDomainsInput(e.target.value) }),
                        React.createElement("p", { className: "text-xs text-gray-500 mt-1" }, "Comma-separated list of domains that can use this token. Use * for wildcards.")))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: handleModalCancel }, "Cancel"),
                React.createElement(Button, { primary: true, disabled: !isFormValid, loading: loading, onClick: handleCreate },
                    React.createElement(Icon, { name: "checkmark" }),
                    "Create"))),
        React.createElement(Confirm, { open: deleteConfirmOpen, header: "Delete API Token", content: "Are you sure you want to delete this API token? Any external integrations using it will stop working immediately.", onCancel: () => {
                setDeleteConfirmOpen(false);
                setTokenToDelete(null);
            }, onConfirm: () => tokenToDelete && handleDelete(tokenToDelete), cancelButton: "Cancel", confirmButton: "Delete" })));
};
export default ApiTokensPage;
